/**
 * SCORM / xAPI lesson block renderer.
 *
 * Renders the package inside a sandboxed iframe and installs `window.API`
 * (SCORM 1.2) and `window.API_1484_11` (SCORM 2004) shims on the *parent*
 * window so the SCO can locate them via the standard
 * `findAPI(window)` recursion. CMI key/value pairs are flushed to
 * `/v1/training/scorm/:packageId/_state` on Commit / Finish / unload.
 *
 * For xAPI packages we install a lightweight `window.XSC_XAPI` helper that
 * forwards statements to `/v1/training/xapi/:packageId/statements`. SCOs
 * built with Articulate Storyline / Rise tincan output use TinCanJS which
 * looks at the `endpoint`/`auth` launch params — we pass them via the
 * iframe URL hash.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api-client.js';
import { getToken } from '../../lib/auth-store.js';
import { normalizeScormScorePct, type ScormCmiState, type ScormStatePatch } from '@xsc/shared';

// Honour `VITE_BASE_PATH` (e.g. `/xsc`) for the iframe `src` and the
// sendBeacon URL — both bypass `api-client` and would otherwise 404 under
// a prefixed deploy.
const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? PATH_PREFIX_NORMALIZED;

interface MetaResponse {
  id: string;
  manifest_version: 'scorm12' | 'scorm2004' | 'xapi';
  launch_path: string;
  title: string | null;
  module_id: string;
  lesson_id: string;
  /** Task #22 — module passing score (0–100), used for the score gate UI. */
  passing_score: number | null;
}

/** Task #22 — snapshot of the learner's current attempt used to render the
 *  score-vs-required banner and the retry button. */
interface AttemptInfo {
  status: ScormCmiState['lesson_status'];
  scorePct: number | null;
}

interface Props {
  packageId: string;
  title?: string;
  /** Optional callback fired the first time the package reports completion
   *  so the surrounding page can invalidate progress queries. */
  onCompleted?: () => void;
}

type CmiBag = Record<string, string>;

const COMPLETED = new Set(['passed', 'completed']);

function parseScore(raw: string): number | null {
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Task #22 — decides whether a flush must hit the server. Non-final flushes
 * are skipped when nothing changed, but a FINAL flush (LMSFinish / Terminate
 * / unload) is always sent — even with an empty patch — because the server
 * holds unscored completions until it sees the attempt's final flush.
 * Exported for tests.
 */
export function planFlush(
  prev: CmiBag,
  next: CmiBag,
  final: boolean,
): { patch: ScormStatePatch; send: boolean } {
  const patch = patchFromCmi(prev, next);
  const hasChanges = !!patch.raw && Object.keys(patch.raw).length > 0;
  return { patch, send: hasChanges || final };
}

const STATUS_KEYS = ['cmi.core.lesson_status', 'cmi.success_status', 'cmi.completion_status'];

/**
 * Task #22 — derives one normalized lesson_status from the full CMI bag with
 * deterministic precedence. SCORM 2004 splits success_status from
 * completion_status; a failed success_status always wins over a completed
 * completion_status, regardless of setter order.
 */
export function deriveLessonStatus(bag: CmiBag): ScormCmiState['lesson_status'] {
  const success = bag['cmi.success_status'];
  if (success === 'failed') return 'failed';
  if (success === 'passed') return 'passed';
  const core = bag['cmi.core.lesson_status'];
  if (core) return core as ScormCmiState['lesson_status'];
  const completion = bag['cmi.completion_status'];
  return (completion ?? null) as ScormCmiState['lesson_status'];
}

/**
 * Task #22 — transport for a flush. On a FINAL flush the beacon is fired
 * SYNCHRONOUSLY, before any await, so the attempt settles server-side even
 * when the page is torn down mid-unload and the regular request never
 * completes. The regular request still runs (when the page survives) so the
 * caller can react to the server's gate verdict. Exported for tests.
 */
export type FinalTransport = 'keepaliveFetch' | 'beacon';

/**
 * Task #22 — picks the unload-safe transport for the final flush.
 * `sendBeacon` cannot carry an Authorization header, so bearer-token
 * sessions (legacy pasted-JWT mode) must use `fetch(..., {keepalive:true})`
 * which survives unload AND authenticates. Cookie sessions use the beacon
 * (cookies ride along automatically) with keepalive fetch as fallback.
 * Exported for tests.
 */
export function chooseFinalTransport(hasBearerToken: boolean, hasBeacon: boolean): FinalTransport {
  if (hasBearerToken) return 'keepaliveFetch';
  return hasBeacon ? 'beacon' : 'keepaliveFetch';
}

export function dispatchFlush<R>(opts: {
  patch: ScormStatePatch;
  final: boolean;
  postUrl: string;
  beaconUrl: string;
  post: (url: string, body: unknown) => Promise<R>;
  /** Fire-and-forget unload-safe transport; must run synchronously. */
  finalSend?: (url: string, body: string) => void;
}): Promise<R | null> {
  if (opts.final && opts.finalSend) {
    try {
      // Fired SYNCHRONOUSLY, before any await, so page teardown cannot
      // lose it.
      opts.finalSend(opts.beaconUrl, JSON.stringify(opts.patch));
    } catch {
      /* best-effort */
    }
  }
  return opts.post(opts.postUrl, opts.patch).catch(() => null);
}

export function patchFromCmi(prev: CmiBag, next: CmiBag): ScormStatePatch {
  const patch: ScormStatePatch = { raw: {} };
  let statusChanged = false;
  for (const [k, v] of Object.entries(next)) {
    if (prev[k] === v) continue;
    patch.raw![k] = v;
    if (STATUS_KEYS.includes(k)) statusChanged = true;
    if (k === 'cmi.core.score.raw' || k === 'cmi.score.raw') {
      patch.score_raw = parseScore(v);
    } else if (k === 'cmi.core.score.min' || k === 'cmi.score.min') {
      patch.score_min = parseScore(v);
    } else if (k === 'cmi.core.score.max' || k === 'cmi.score.max') {
      patch.score_max = parseScore(v);
    } else if (k === 'cmi.suspend_data') {
      patch.suspend_data = v;
    } else if (k === 'cmi.core.total_time' || k === 'cmi.total_time') {
      patch.total_time = v;
    }
  }
  if (statusChanged) {
    patch.lesson_status = deriveLessonStatus(next);
  }
  return patch;
}

function attemptFromState(state: {
  lesson_status: ScormCmiState['lesson_status'];
  score_raw: number | null;
  score_min: number | null;
  score_max: number | null;
}): AttemptInfo {
  return {
    status: state.lesson_status,
    scorePct: normalizeScormScorePct(state.score_raw, state.score_min, state.score_max),
  };
}

function attemptFromCmi(bag: CmiBag): AttemptInfo {
  const status = deriveLessonStatus(bag);
  const num = (k12: string, k2004: string): number | null => {
    const v = bag[k12] ?? bag[k2004];
    return v !== undefined ? parseScore(v) : null;
  };
  return {
    status,
    scorePct: normalizeScormScorePct(
      num('cmi.core.score.raw', 'cmi.score.raw'),
      num('cmi.core.score.min', 'cmi.score.min'),
      num('cmi.core.score.max', 'cmi.score.max'),
    ),
  };
}

export function ScormBlockView({ packageId, title, onCompleted }: Props): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const completedRef = useRef(false);
  const cmiRef = useRef<CmiBag>({});
  const lastSavedRef = useRef<CmiBag>({});
  const [attempt, setAttempt] = useState<AttemptInfo | null>(null);
  const [passingScore, setPassingScore] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [attemptNonce, setAttemptNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let metaUrl = '';
    let manifestVersion: MetaResponse['manifest_version'] = 'scorm12';

    const flush = async (final: boolean) => {
      const { patch, send } = planFlush(lastSavedRef.current, cmiRef.current, final);
      if (!send) return;
      // Task #22 — `?final=1` tells the server this is the attempt's last
      // flush so an unscored completion can settle as an ungraded package.
      // Final flushes are ALWAYS sent, even with an empty patch, so the
      // server re-evaluates the merged state as final.
      const stateUrl = `/v1/training/scorm/${encodeURIComponent(packageId)}/_state${final ? '?final=1' : ''}`;
      // On final flushes the beacon fires synchronously inside dispatchFlush
      // BEFORE the awaited request, so unload teardown cannot lose it.
      const token = getToken();
      const transport = chooseFinalTransport(!!token, !!navigator.sendBeacon);
      const finalSend = (url: string, body: string) => {
        if (transport === 'beacon') {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } else {
          // keepalive fetch survives unload AND can carry the bearer token,
          // which sendBeacon cannot.
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;
          void fetch(url, {
            method: 'POST',
            headers,
            body,
            keepalive: true,
            credentials: 'include',
          }).catch(() => {});
        }
      };
      const res = await dispatchFlush<{ state: ScormCmiState; gate?: string }>({
        patch,
        final,
        postUrl: stateUrl,
        beaconUrl: `${BASE_URL}/v1/training/scorm/${encodeURIComponent(packageId)}/_state?final=1`,
        post: (url, body) => api.post(url, body),
        finalSend,
      });
      if (res) {
        lastSavedRef.current = { ...cmiRef.current };
        setAttempt(attemptFromCmi(cmiRef.current));
        // Completion is driven by the SERVER's gate verdict, not the local
        // status patch — held or failing attempts must not refresh progress.
        if (res.gate === 'complete' && !completedRef.current) {
          completedRef.current = true;
          onCompleted?.();
        }
      }
    };

    // SCORM 1.2 API surface ---------------------------------------------------
    const scorm12: Record<string, (a?: string, b?: string) => string> = {
      LMSInitialize: () => 'true',
      LMSFinish: () => {
        void flush(true);
        return 'true';
      },
      LMSGetValue: (key) => cmiRef.current[key ?? ''] ?? '',
      LMSSetValue: (key, value) => {
        if (!key) return 'false';
        cmiRef.current[key] = value ?? '';
        return 'true';
      },
      LMSCommit: () => {
        void flush(false);
        return 'true';
      },
      LMSGetLastError: () => '0',
      LMSGetErrorString: () => 'No error',
      LMSGetDiagnostic: () => '',
    };

    // SCORM 2004 API surface --------------------------------------------------
    const scorm2004: Record<string, (a?: string, b?: string) => string> = {
      Initialize: () => 'true',
      Terminate: () => {
        void flush(true);
        return 'true';
      },
      GetValue: (key) => cmiRef.current[key ?? ''] ?? '',
      SetValue: (key, value) => {
        if (!key) return 'false';
        cmiRef.current[key] = value ?? '';
        return 'true';
      },
      Commit: () => {
        void flush(false);
        return 'true';
      },
      GetLastError: () => '0',
      GetErrorString: () => 'No error',
      GetDiagnostic: () => '',
    };

    const w = window as unknown as Record<string, unknown>;
    const prev12 = w.API;
    const prev2004 = w.API_1484_11;
    w.API = scorm12;
    w.API_1484_11 = scorm2004;

    // xAPI bridge — exposed for tincan/cmi5 packages that read it instead
    // of going through TinCanJS LRS config.
    w.XSC_XAPI = {
      sendStatement: async (statement: unknown) => {
        try {
          const res = await api.post<{ gate?: string | null }>(
            `/v1/training/xapi/${encodeURIComponent(packageId)}/statements`,
            statement,
          );
          // Task #22 — completion is driven by the server's FINAL aggregate
          // gate verdict for the batch, so a [completed, failed] batch never
          // triggers a misleading progress refresh.
          if (res.gate === 'complete' && !completedRef.current) {
            completedRef.current = true;
            onCompleted?.();
          }
        } catch {
          /* swallow */
        }
      },
    };

    const onUnload = () => {
      void flush(true);
    };
    window.addEventListener('beforeunload', onUnload);

    (async () => {
      const meta = await api.get<MetaResponse>(
        `/v1/training/scorm/${encodeURIComponent(packageId)}/_meta`,
      );
      if (cancelled) return;
      manifestVersion = meta.manifest_version;
      setPassingScore(meta.passing_score ?? null);
      const launch = meta.launch_path.replace(/^\/+/, '');
      let src = `${BASE_URL}/v1/training/scorm/${encodeURIComponent(packageId)}/${launch}`;
      if (manifestVersion === 'xapi') {
        // TinCanJS-style launch params: actor + endpoint + auth. We pass
        // them in the query string of the iframe URL; the API server will
        // ignore unknown params when serving the file.
        const endpoint = `${window.location.origin}${BASE_URL}/v1/training/xapi/${encodeURIComponent(packageId)}/`;
        const actor = encodeURIComponent(
          JSON.stringify({ account: { name: 'learner', homePage: window.location.origin } }),
        );
        const sep = src.includes('?') ? '&' : '?';
        src = `${src}${sep}endpoint=${encodeURIComponent(endpoint)}&actor=${actor}&auth=cookie`;
      }
      metaUrl = src;
      // Prime cmi state from server so resume works.
      try {
        const { state } = await api.get<{ state: ScormCmiState }>(
          `/v1/training/scorm/${encodeURIComponent(packageId)}/_state`,
        );
        if (state && state.raw) {
          cmiRef.current = { ...state.raw };
          lastSavedRef.current = { ...state.raw };
          if (state.lesson_status && COMPLETED.has(state.lesson_status)) {
            completedRef.current = true;
          }
          if (state.lesson_status || state.score_raw !== null) {
            setAttempt(attemptFromState(state));
          }
        }
      } catch {
        /* fresh launch */
      }
      if (iframeRef.current) iframeRef.current.src = metaUrl;
    })().catch(() => {
      /* surfaced via the empty iframe below */
    });

    // Periodic flush in case the SCO never calls Commit (some auth tools
    // only Commit on Terminate, which can be missed on tab-close).
    const interval = window.setInterval(() => {
      void flush(false);
    }, 15_000);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', onUnload);
      window.clearInterval(interval);
      // Restore any prior globals so we don't bleed shims between lessons.
      if (prev12 === undefined) delete w.API;
      else w.API = prev12;
      if (prev2004 === undefined) delete w.API_1484_11;
      else w.API_1484_11 = prev2004;
      delete w.XSC_XAPI;
    };
  }, [packageId, onCompleted, attemptNonce]);

  // Task #22 — retry: wipe server-side CMI state, clear local state, and
  // relaunch the iframe for a fresh attempt.
  const retryAttempt = async () => {
    setResetting(true);
    try {
      await api.delete(`/v1/training/scorm/${encodeURIComponent(packageId)}/_state`);
      cmiRef.current = {};
      lastSavedRef.current = {};
      completedRef.current = false;
      setAttempt(null);
      setAttemptNonce((n) => n + 1);
    } catch {
      /* leave current attempt in place; the learner can retry the reset */
    } finally {
      setResetting(false);
    }
  };

  const hasScore = attempt?.scorePct !== null && attempt?.scorePct !== undefined;
  const passed = attempt?.status ? COMPLETED.has(attempt.status) : false;
  const failedGate =
    attempt !== null &&
    (attempt.status === 'failed' ||
      (hasScore && passingScore !== null && attempt.scorePct! < passingScore));

  return (
    <figure className="my-3">
      {title && <figcaption className="font-semibold mb-2">{title}</figcaption>}
      <div className="aspect-video w-full rounded overflow-hidden border border-border bg-black">
        <iframe
          ref={iframeRef}
          title={title ?? 'SCORM content'}
          className="w-full h-full"
          // Allow same-origin so the SCO can call window.parent.API; the
          // server CSP locks down frame-ancestors to 'self'.
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-presentation"
        />
      </div>
      {attempt !== null && (hasScore || attempt.status) && (
        <div
          className={`mt-2 rounded border px-3 py-2 text-sm flex items-center justify-between gap-2 flex-wrap ${
            failedGate
              ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700'
              : 'border-border bg-surface-alt'
          }`}
          role="status"
        >
          <span>
            {hasScore ? (
              <>
                Score: <strong>{Math.round(attempt.scorePct!)}%</strong>
                {passingScore !== null && <> · Required: {passingScore}%</>}
              </>
            ) : (
              <>Status: {attempt.status}</>
            )}
            {failedGate && !passed && (
              <span className="ml-1">
                — below the passing threshold. Retry to try again.
              </span>
            )}
          </span>
          {failedGate && (
            <button
              type="button"
              onClick={() => void retryAttempt()}
              disabled={resetting}
              className="rounded border border-border px-2 py-1 text-xs font-semibold hover:bg-surface disabled:opacity-50"
            >
              {resetting ? 'Resetting…' : 'Retry attempt'}
            </button>
          )}
        </div>
      )}
    </figure>
  );
}
